import { useEvent } from "shared/lib/useEvent"
import { useScreenSize } from "shared/lib/useScreenSize"
import { Action } from "shared/types/Action"
import { Room } from "shared/types/Room"
import { RowRendererProps } from "shared/ui/table/types"

import { useProjectPermissions } from "entities/projects/model/useProjectPermissions"
import { readonlyProjectPermissions } from "entities/projects/types/ProjectPermissions"
import { useProjectStore } from "features/project-management/model/useProjectStore"
import { useRoomActionHandlers } from "features/room-management/model/useRoomActionHandlers"
import { useRoomActions } from "features/room-management/model/useRoomActions"
import { RoomFormTabType } from "features/room-management/types/RoomFormTabType"
import { useProductPositionTableRowPayload } from "widgets/product-position-table/contexts/ProductPositionTableRowPayloadContext"

export interface ProductPositionTableRoomRowModelResult
  extends RowRendererProps<Room> {
  actions: Action[]
}

export function useProductPositionTableRoomRowModel(
  props: RowRendererProps<Room>
): ProductPositionTableRoomRowModelResult {
  const { item } = props
  const { projectID, isReadonly } = useProductPositionTableRowPayload()

  const { isDesktop } = useScreenSize()
  const { selectElement, enterSidebarEditing, setSidebarActiveTab } =
    useProjectStore(projectID ?? "")

  const { permissions } = useProjectPermissions({
    projectID: projectID ?? ""
  })

  const handlers = useRoomActionHandlers({
    projectID: projectID ?? "",
    roomID: item.id,
    permissions: permissions ?? readonlyProjectPermissions
  })

  const handleEdit = useEvent(() => {
    if (isDesktop) {
      selectElement(item.id, { preventUnselectOnReselect: true })
      enterSidebarEditing()
    } else {
      handlers.onEdit?.()
    }
  })

  const handleAssignAttachments = useEvent(() => {
    if (isDesktop) {
      selectElement(item.id, { preventUnselectOnReselect: true })
      enterSidebarEditing()
      setSidebarActiveTab(RoomFormTabType.ATTACHMENTS)
    } else {
      handlers.onAssignAttachments?.()
    }
  })

  const handleAddClarificationRequest = useEvent(() => {
    if (isDesktop) {
      selectElement(item.id, { preventUnselectOnReselect: true })
      enterSidebarEditing()
      setSidebarActiveTab(RoomFormTabType.CLARIFICATION_REQUEST)
    } else {
      handlers.onAddClarificationRequest?.()
    }
  })

  const { actions } = useRoomActions({
    idPrefix: `room-row-${item.id}`,
    isReadonly,
    ...handlers,
    onEdit: handlers.onEdit ? handleEdit : undefined,
    onAssignAttachments: handlers.onAssignAttachments
      ? handleAssignAttachments
      : undefined,
    onAddClarificationRequest: handlers.onAddClarificationRequest
      ? handleAddClarificationRequest
      : undefined
  })

  return {
    ...props,
    actions
  }
}
